import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography,
  TextField, CircularProgress, Tooltip, Stack, IconButton, Button, alpha,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../../../redux/actions';

// ตาราง matrix เช็คอินย้อนหลัง — component ใหม่ทั้งหมด เขียนขึ้นเองโดยเฉพาะ ไม่ได้แตะ/ไม่ได้
// reuse component หรือ logic ใดๆ จาก MergedPayrollTableV2.js เลยตามที่ user ย้ำไว้ — หยิบมาแค่
// แนวคิดเลย์เอาต์ (แถว=รายการ, คอลัมน์=วันที่ในเดือน) มาออกแบบใหม่ ไม่มีเรื่องเงินเดือน/OT ปนเลย
//
// โครงสร้าง: แถว = โปรเจกต์ที่มีคนเช็คอินอย่างน้อย 1 ครั้งในเดือนที่เลือก, คอลัมน์ = วันที่ 1
// ถึงวันสุดท้ายของเดือน, เซลล์ = ชื่อคนที่เช็คอินโปรเจกต์แถวนั้นในวันนั้น (ว่าง = ไม่มีใครเช็คอิน)
// คอลัมน์ "รวม" ท้ายสุด = จำนวนวันที่มีคนเช็คอินโปรเจกต์แถวนั้นในเดือนนี้ — ความกว้างคอลัมน์วัน
// ปรับตามเนื้อหา (วันที่มีคนเช็คอินจะกว้างกว่าวันว่างเปล่า เพื่อยัดชื่อคนได้พอดี)
const CELL_MIN_WIDTH = 40;
const CHAR_WIDTH_PX = 7.5;
const CELL_PADDING = 20;

// เส้นตาราง (column + row divider) ใช้ร่วมกันทุกเซลล์ — บอร์เดอร์ขวา+ล่างของทุกเซลล์ประกบกันเป็น
// เส้นกริดเดียว (Table ตั้ง borderCollapse: 'collapse' คู่กัน กันเส้นหนาสองชั้น)
const GRID_LINE = { borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider' };

export function CheckinHistoryMatrix({ ownerNameById }) {
  const dispatch = useDispatch();
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [checkins, setCheckins] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const start = dayjs(`${month}-01`);
    dispatch(actions.checkinHistory({
      dateFrom: start.format('YYYY-MM-DD'),
      dateTo: start.endOf('month').format('YYYY-MM-DD'),
    }))
      .then((data) => { if (!cancelled) setCheckins(data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, month]);

  const days = useMemo(() => {
    const start = dayjs(`${month}-01`);
    return Array.from({ length: start.daysInMonth() }, (_, i) => start.date(i + 1));
  }, [month]);

  const { rows, colWidths } = useMemo(() => {
    const projectMap = {};
    (checkins || []).forEach((c) => {
      const oppId = c.opportunity_id;
      if (!projectMap[oppId]) {
        projectMap[oppId] = {
          opportunityId: oppId,
          projectName: c.marketing_opportunities?.project_name || '-',
          documentNo: c.marketing_opportunities?.opportunity_number || '',
          cells: {},
        };
      }
      const dayKey = dayjs(c.checkin_date).format('D');
      const name = ownerNameById[c.employee_id] || '...';
      if (!projectMap[oppId].cells[dayKey]) projectMap[oppId].cells[dayKey] = [];
      projectMap[oppId].cells[dayKey].push({ name, planNote: c.plan_note || '' });
    });

    const rowsArr = Object.values(projectMap)
      .sort((a, b) => a.projectName.localeCompare(b.projectName, 'th'));

    const widths = {};
    days.forEach((d) => {
      const key = d.format('D');
      let maxLen = 0;
      rowsArr.forEach((row) => {
        const text = (row.cells[key] || []).map((e) => e.name).join(', ');
        if (text.length > maxLen) maxLen = text.length;
      });
      widths[key] = maxLen === 0
        ? CELL_MIN_WIDTH
        : Math.max(CELL_MIN_WIDTH, Math.ceil(maxLen * CHAR_WIDTH_PX) + CELL_PADDING);
    });

    return { rows: rowsArr, colWidths: widths };
  }, [checkins, days, ownerNameById]);

  const goToMonth = (delta) => setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));
  const isCurrentMonth = month === dayjs().format('YYYY-MM');

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => goToMonth(-1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <TextField
          size="small"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />
        <IconButton size="small" onClick={() => goToMonth(1)} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {!isCurrentMonth && (
          <Button size="small" variant="text" onClick={() => setMonth(dayjs().format('YYYY-MM'))} sx={{ textTransform: 'none', borderRadius: 2 }}>
            เดือนนี้
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="body2">ไม่มีเช็คอินในเดือนนี้</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 560, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...GRID_LINE,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    minWidth: 200,
                    width: 200,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    bgcolor: 'background.paper',
                  }}
                >
                  โปรเจกต์
                </TableCell>
                <TableCell sx={{ ...GRID_LINE, fontWeight: 700, fontSize: '0.75rem', minWidth: 60, width: 60, textAlign: 'center' }}>
                  รวม
                </TableCell>
                {days.map((d) => {
                  const isSun = d.day() === 0;
                  const isSat = d.day() === 6;
                  return (
                    <TableCell
                      key={d.format('D')}
                      sx={{
                        ...GRID_LINE,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        minWidth: colWidths[d.format('D')],
                        width: colWidths[d.format('D')],
                        textAlign: 'center',
                        color: isSun ? 'error.main' : isSat ? 'info.main' : 'text.secondary',
                        bgcolor: (theme) => ((isSun || isSat) ? alpha(theme.palette.text.primary, 0.03) : 'transparent'),
                      }}
                    >
                      {d.format('D')}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIdx) => {
                const total = days.filter((d) => row.cells[d.format('D')]?.length).length;
                const stripeBg = rowIdx % 2 === 1 ? 'action.hover' : 'transparent';
                return (
                  <TableRow key={row.opportunityId} hover sx={{ bgcolor: stripeBg }}>
                    <TableCell
                      sx={{
                        ...GRID_LINE,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: rowIdx % 2 === 1 ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.projectName}
                      </Typography>
                      {row.documentNo && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            fontSize: '0.68rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.documentNo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...GRID_LINE, textAlign: 'center', fontSize: '0.75rem' }}>
                      {total > 0 ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            minWidth: 22,
                            height: 20,
                            px: 0.75,
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 999,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            color: 'primary.main',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                          }}
                        >
                          {total}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">0</Typography>
                      )}
                    </TableCell>
                    {days.map((d) => {
                      const entries = row.cells[d.format('D')];
                      if (!entries || entries.length === 0) {
                        return <TableCell key={d.format('D')} sx={GRID_LINE} />;
                      }
                      const tooltipText = entries
                        .map((e) => (e.planNote ? `${e.name}: ${e.planNote}` : e.name))
                        .join('\n');
                      return (
                        <TableCell key={d.format('D')} sx={{ ...GRID_LINE, fontSize: '0.7rem' }}>
                          <Tooltip title={tooltipText} sx={{ whiteSpace: 'pre-line' }}>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                maxWidth: '100%',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                                color: 'success.main',
                                fontWeight: 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {entries.map((e) => e.name).join(', ')}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default CheckinHistoryMatrix;
